/**
 * Rendert eine vom Backend gelieferte Sektionsliste in die bekannten Design-Komponenten.
 * Niemals rohes HTML aus Backend rendern.
 */
import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { m as motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Phone, Star, Sparkles, ShieldCheck } from "lucide-react";
import ProductCard from "@/components/common/ProductCard";
import { CatalogLoadState } from "@/components/common/CatalogLoadState";
import { useCatalog, productImage } from "@/lib/catalog";
import { HOME_RENDERERS, BARE_TYPES } from "@/components/home/HomeSections";
import { HeroPayStrip } from "@/components/pay/PayOptions";
import { imgSet } from "@/lib/img";

const BG_CLS = {
  white: "bg-white",
  cream: "bg-cream",
  warm: "bg-warm",
  ink: "bg-ink text-white",
};
const WIDTH_CLS = {
  default: "max-w-[1200px]",
  wide: "max-w-[1400px]",
  full: "max-w-none",
};
const SPACING_CLS = {
  compact: "py-8 md:py-12",
  standard: "py-16 md:py-20",
  large: "py-24 md:py-32",
};

function SectionWrapper({ section, children }) {
  const bg = BG_CLS[section.background] || BG_CLS.white;
  const w = WIDTH_CLS[section.width] || WIDTH_CLS.default;
  // Kopfbereich ohne große Lücke unter der Navigation (31.10.2026)
  const sp = section.type === "hero" ? "pt-6 md:pt-8 pb-16 md:pb-24" : (SPACING_CLS[section.spacing] || SPACING_CLS.standard);
  return (
    <section className={`${bg} ${sp} border-b border-hair`} data-section-type={section.type}>
      <div className={`${w} mx-auto px-6 md:px-10`}>{children}</div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, title_accent, subtitle, className = "" }) {
  return (
    <div className={`max-w-3xl ${className}`}>
      {eyebrow && <div className="pill-badge">{eyebrow}</div>}
      {(title || title_accent) && (
        <h2 className="font-heading text-4xl md:text-5xl leading-[1.05] mt-4">
          {title} {title_accent && <span className="font-serif-italic text-brand-yellow">{title_accent}</span>}
        </h2>
      )}
      {subtitle && <p className="mt-4 text-lg text-ink-soft">{subtitle}</p>}
    </div>
  );
}

// ---- Sektions-Renderer ----
// Premium Hero mit echten Produktbildern (aus useCatalog) und Framer-Motion Animationen.
// Kein Stock-Foto, keine erfundenen Bilder, nur eigene Katalog-Assets.
function HeroSection({ section }) {
  const { pathname } = useLocation();
  const d = section.data || {};
  const { products, loading, error, retry } = useCatalog();

  // Priorisierte Produkt-Slugs für die Hero-Collage (Reihenfolge = Z-Stapel).
  // Fallback: erste 4 Produkte aus Katalog, wenn Slugs nicht gefunden werden.
  const heroSlugs = d.product_slugs && d.product_slugs.length
    ? d.product_slugs
    : ["fotobox-topbase", "magic-mirror", "fotobox-premium", "360-videobooth"];
  const heroProducts = heroSlugs
    .map((s) => products.find((p) => p.slug === s))
    .filter(Boolean)
    .slice(0, 4);
  const displayProducts = heroProducts.length >= 3 ? heroProducts : products.slice(0, 4);

  return (
    <div className="relative overflow-hidden">
      {/* Ambient Glow: dezent, kein Bild */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -mx-6 md:-mx-10"
        style={{ background: "radial-gradient(45% 55% at 78% 32%, rgba(244,184,74,0.22) 0%, rgba(244,184,74,0.06) 45%, rgba(253,250,242,0) 78%), radial-gradient(35% 40% at 12% 68%, rgba(232,168,61,0.14) 0%, rgba(253,250,242,0) 70%)" }} />

      <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-16 items-center min-h-[560px] lg:min-h-[640px]">
        {/* LEFT: Text-Block, linksbündig, premium */}
        <div className="text-left">
          {d.eyebrow && (
            <motion.div initial={{ y: 8 }} animate={{ y: 0 }} transition={{ duration: 0.55 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/85 backdrop-blur border border-brand/25 shadow-sm">
              <Sparkles className="w-3.5 h-3.5" style={{ color: "rgb(var(--brand-text-rgb))" }} />
              <span className="text-[10px] tracking-[0.22em] uppercase font-semibold font-body text-ink">{d.eyebrow}</span>
            </motion.div>
          )}

          <motion.h1 initial={{ y: 24 }} animate={{ y: 0 }} transition={{ duration: 0.85, delay: 0.05 }}
            className="font-heading text-[42px] md:text-[64px] lg:text-[80px] leading-[1.02] text-ink mt-6 tracking-tight">
            {d.title}
            {d.title_accent && <>{" "}<br /><span className="font-serif-italic text-brand-yellow">{d.title_accent}</span></>}
          </motion.h1>

          {d.subtitle && (
            <motion.p initial={{ y: 16 }} animate={{ y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-5 text-lg md:text-xl text-ink-soft max-w-xl leading-relaxed">
              {d.subtitle}
            </motion.p>
          )}
          {d.description && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-4 text-base text-ink-soft max-w-xl leading-relaxed">
              {d.description}
            </motion.p>
          )}

          <motion.div initial={{ y: 12 }} animate={{ y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-9 flex flex-wrap gap-3">
            {d.primary_cta_label && (
              <Link to={d.primary_cta_href || "/fotoboxen"} className="btn-accent !py-4 !px-7 shadow-xl shadow-brand-yellow/20" data-testid="hero-primary-cta">
                {d.primary_cta_label} <ArrowRight className="w-4 h-4"/>
              </Link>
            )}
            {d.secondary_cta_label && ((d.secondary_cta_href || "").startsWith("http")
              ? <a href={d.secondary_cta_href} target="_blank" rel="noreferrer" className="btn-secondary !py-4 !bg-white/75 !border-hair" data-testid="hero-secondary-cta">
                  {d.secondary_cta_label}
                </a>
              : <Link to={d.secondary_cta_href || "#"} className="btn-secondary !py-4 !bg-white/75 !border-hair" data-testid="hero-secondary-cta">
                  {d.secondary_cta_label}
                </Link>
            )}
          </motion.div>

          {pathname === "/" && <HeroPayStrip />}

          {/* Trust-Row: dezent, kein Boxen-Look (Texte aus d.trust_chips, sonst Standard) */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-muted">
            {(d.trust_chips && d.trust_chips.length ? d.trust_chips : ["Gesetzliche Mängelhaftung", "Vormontiert & getestet", "Deutscher Fachhandel"]).map((c, i) => (
              <span key={c} className="inline-flex items-center gap-2">
                {i === 0 && !d.trust_chips ? <ShieldCheck className="w-4 h-4 text-brand-yellow" strokeWidth={2.2} /> : <Check className="w-4 h-4 text-brand-yellow" strokeWidth={3} />} {c}
              </span>
            ))}
          </motion.div>
        </div>

        {/* RIGHT: Produkt-Collage mit echten Katalog-Bildern (Desktop) bzw. 2×2-Raster (Handy) */}
        <div>
          <CatalogLoadState loading={loading} error={error} onRetry={retry} id={`home-hero-products-${section.id}`} />
          {!loading && !error && (
            <>
              <div className="hidden md:block"><HeroProductCollage products={displayProducts} /></div>
              <div className="md:hidden grid grid-cols-2 gap-3" data-testid="hero-mobile-products">
                {displayProducts.map((p) => (
                  <Link key={p.slug} to={`/produkte/${p.slug}`} className="aspect-square flex items-center justify-center">
                    <img {...imgSet(productImage(p), "(max-width: 768px) 100vw, 50vw")} alt={p.name} loading="eager" className="max-w-full max-h-full object-contain" />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Freistehende, animierte Collage aus echten Produktbildern.
// Bilder werden nur skaliert/positioniert, keine Verzerrung, keine Ersetzung.
function HeroProductCollage({ products }) {
  const items = (products || []).filter(Boolean).slice(0, 4);
  if (!items.length) return <div className="hidden lg:block h-[520px]" />;

  // Positions-Preset für 3-4 Produkte
  const positions = [
    { top: "8%",  left: "34%", w: "58%", z: 30, delay: 0.1, float: 8 },
    { top: "22%", left: "0%",  w: "44%", z: 20, delay: 0.25, float: -10 },
    { top: "58%", left: "48%", w: "40%", z: 25, delay: 0.4, float: 6 },
    { top: "62%", left: "8%",  w: "34%", z: 15, delay: 0.55, float: -6 },
  ];

  return (
    <div className="relative h-[440px] md:h-[520px] lg:h-[600px]">
      {/* Gold-Radialschein hinter der Collage für Tiefe */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(50% 55% at 55% 45%, rgba(244,184,74,0.25) 0%, transparent 70%)", filter: "blur(20px)" }} />

      {items.map((p, i) => {
        const pos = positions[i % positions.length];
        const img = productImage(p);
        return (
          <motion.div
            key={p.slug}
            initial={{ y: 30, scale: 0.94 }}
            animate={{ y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: pos.delay, ease: [0.16, 1, 0.3, 1] }}
            className="absolute"
            style={{ top: pos.top, left: pos.left, width: pos.w, zIndex: pos.z }}
          >
            <motion.div
              animate={{ y: [0, pos.float, 0] }}
              transition={{ duration: 6 + (i * 0.7), repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              {/* Weicher Schatten unter jedem Bild */}
              <div aria-hidden className="absolute -bottom-4 left-[10%] right-[10%] h-6 rounded-full"
                style={{ background: "radial-gradient(50% 100% at 50% 0%, rgba(20,18,15,0.28) 0%, rgba(20,18,15,0) 75%)", filter: "blur(6px)" }} />
              <img
                {...imgSet(img, "(max-width: 768px) 100vw, 50vw")}
                alt={p.name}
                loading="eager"
                className="w-full h-auto object-contain drop-shadow-[0_16px_40px_rgba(30,25,20,0.16)]"
                style={{ maxHeight: "460px" }}
              />
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
}

function TextSection({ section }) {
  const d = section.data || {};
  return (
    <div className={d.align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} className={d.align === "center" ? "mx-auto" : ""} />
      {d.text && <p className="mt-6 text-lg text-ink-soft leading-relaxed whitespace-pre-wrap">{d.text}</p>}
    </div>
  );
}

function TextImageSection({ section }) {
  const d = section.data || {};
  const imgLeft = d.image_position === "left";
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${imgLeft ? "" : ""}`}>
      <div className={imgLeft ? "lg:order-2" : ""}>
        <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} />
        {d.text && <p className="mt-6 text-lg text-ink-soft leading-relaxed">{d.text}</p>}
        {d.cta_label && <Link to={d.cta_href || "#"} className="btn-accent mt-6">{d.cta_label} <ArrowRight className="w-4 h-4"/></Link>}
      </div>
      <div className={imgLeft ? "lg:order-1" : ""}>
        {d.image_url && <img {...imgSet(d.image_url, "(max-width: 768px) 100vw, 50vw")} alt="" className="w-full rounded-2xl" />}
      </div>
    </div>
  );
}

function ProductGridSection({ section }) {
  const d = section.data || {};
  const { products, loading, error, retry } = useCatalog();
  const list = (d.product_slugs || []).map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  const cols = d.columns === 4 ? "lg:grid-cols-4" : d.columns === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";
  return (
    <div>
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} subtitle={d.subtitle} />
      <CatalogLoadState loading={loading} error={error} onRetry={retry} id={`home-product-grid-${section.id}`} />
      {!loading && !error && <div className={`mt-10 grid grid-cols-1 md:grid-cols-2 ${cols} gap-6`}>
        {list.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
      </div>}
      {d.show_cta && d.cta_label && <div className="mt-10"><Link to={d.cta_href || "/fotoboxen"} className="btn-outline-yellow">{d.cta_label} <ArrowRight className="w-4 h-4"/></Link></div>}
    </div>
  );
}

function FeaturesSection({ section }) {
  const d = section.data || {};
  return (
    <div>
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} subtitle={d.subtitle} />
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {(d.items || []).map((it, i) => (
          <div key={i} className="card-clean p-5">
            <div className="w-10 h-10 rounded-full bg-brand-softer flex items-center justify-center mb-3">
              <Check className="w-5 h-5" style={{ color: "rgb(var(--brand-text-rgb))" }} strokeWidth={3}/>
            </div>
            <div className="font-heading text-xl text-ink">{it.title}</div>
            {it.text && <div className="text-sm text-ink-soft mt-2">{it.text}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqSection({ section }) {
  const d = section.data || {};
  const [open, setOpen] = React.useState(-1);
  return (
    <div>
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} />
      <div className="mt-8 max-w-3xl divide-y divide-hair">
        {(d.items || []).map((it, i) => (
          <button key={i} type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full text-left py-5 group">
            <div className="flex items-start justify-between gap-4">
              <div className="font-medium text-ink">{it.q}</div>
              <span className={`text-2xl transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
            </div>
            {open === i && <div className="mt-3 text-ink-soft">{it.a}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Premium CTA-Band mit garantiertem Kontrast.
// - Wenn section.background === "ink": voll dunkel, weiße Typo, Gold-Radial.
// - Sonst: dunkle Karten-Insel auf hellem Section-Hintergrund (kein weißer Text auf weiß).
function CtaBandSection({ section }) {
  const d = section.data || {};
  const isInkSection = section.background === "ink";

  return (
    <div className="relative">
      <div
        className={`contrast-dark relative overflow-hidden rounded-3xl ${isInkSection ? "" : "shadow-[0_30px_80px_-40px_rgba(20,18,15,0.4)]"} p-10 md:p-16 lg:p-20`}
        style={{
          background: "linear-gradient(135deg, #14120F 0%, #1F1B15 55%, #2A2118 100%)",
          border: "1px solid rgba(244,184,74,0.18)",
        }}
      >
        {/* Gold-Radial Glow */}
        <div aria-hidden className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(60% 60% at 78% 30%, rgba(244,184,74,0.35) 0%, transparent 60%), radial-gradient(50% 50% at 12% 78%, rgba(232,168,61,0.18) 0%, transparent 65%)" }} />
        {/* Feine Grain-Textur für Tiefe */}
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

        <div className="relative text-center max-w-3xl mx-auto">
          {d.eyebrow && (
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 text-brand-yellow text-[10px] tracking-[0.24em] uppercase font-semibold mb-6">
              <Sparkles className="w-3 h-3" /> {d.eyebrow}
            </div>
          )}
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-white">
            {d.title}{" "}
            {d.title_accent && (
              <span className="font-serif-italic text-brand-yellow">{d.title_accent}</span>
            )}
          </h2>
          {d.subtitle && (
            <p className="mt-5 max-w-2xl mx-auto text-lg text-white/80 leading-relaxed">
              {d.subtitle}
            </p>
          )}
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            {d.primary_cta_label && (
              <Link to={d.primary_cta_href || "/fotoboxen"} className="btn-accent !py-4 !px-7 shadow-2xl shadow-brand-yellow/40" data-testid="cta-band-primary">
                {d.primary_cta_label} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {d.secondary_cta_label && (
              (d.secondary_cta_href || "").startsWith("http")
                ? <a href={d.secondary_cta_href} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/25 text-white hover:bg-white/10 transition-colors text-sm font-medium">
                    {d.secondary_cta_label}
                  </a>
                : <Link to={d.secondary_cta_href || "#"}
                     className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/25 text-white hover:bg-white/10 transition-colors text-sm font-medium">
                    {d.secondary_cta_label}
                  </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsSection({ section }) {
  const d = section.data || {};
  return (
    <div>
      {d.title && <h2 className="font-heading text-4xl text-ink mb-8">{d.title}</h2>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {(d.items || []).map((it, i) => (
          <div key={i}>
            <div className="font-heading text-5xl text-ink">{it.value}</div>
            <div className="text-sm text-ink-muted mt-2">{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpacerSection({ section }) {
  const h = section.data?.size === "large" ? "h-32" : section.data?.size === "small" ? "h-8" : "h-16";
  return <div className={h} />;
}

function ProductFinderTeaser({ section }) {
  const d = section.data || {};
  return (
    <div className="text-center max-w-2xl mx-auto">
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent="passt zu Ihnen?" className="mx-auto text-center" />
      <Link to="/finder" className="btn-accent mt-6">{d.cta_label || "Zum Produktfinder"} <ArrowRight className="w-4 h-4"/></Link>
    </div>
  );
}

// ---- Marquee (endloser horizontaler Ticker) ----
function MarqueeSection({ section }) {
  const d = section.data || {};
  const items = d.items || [];
  const duration = d.speed === "fast" ? "20s" : d.speed === "slow" ? "60s" : "40s";
  // Doppelte Liste für nahtloses Loop
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden relative" style={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}>
      <div className="flex gap-10 whitespace-nowrap animate-marquee" style={{ animationDuration: duration }}>
        {doubled.map((it, i) => (
          <span key={i} className="font-heading text-2xl md:text-3xl text-ink-soft/70 italic">{it}</span>
        ))}
      </div>
    </div>
  );
}

// ---- Steps (3-Schritte-Prozess) ----
function StepsSection({ section }) {
  const d = section.data || {};
  return (
    <div>
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} subtitle={d.subtitle} />
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
        {(d.items || []).map((it, i) => (
          <div key={i} className="relative">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 shrink-0 rounded-full bg-ink text-brand-yellow font-heading text-2xl font-semibold flex items-center justify-center shadow-lg ring-4 ring-brand-yellow/25">
                {i + 1}
              </div>
              <div>
                <div className="font-heading text-2xl text-ink leading-tight">{it.title}</div>
                {it.text && <div className="mt-2 text-ink-soft leading-relaxed">{it.text}</div>}
              </div>
            </div>
            {i < (d.items || []).length - 1 && (
              <div className="hidden md:block absolute right-0 top-6 w-12 h-px bg-hair" style={{ transform: "translateX(50%)" }} />
            )}
          </div>
        ))}
      </div>
      {(d.packages || []).length > 0 && (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="steps-packages">
          {d.packages.map((pk) => (
            <div key={pk.name} className={`relative rounded-2xl bg-white p-5 md:p-6 ${pk.highlight ? "border-2 border-brand-yellow shadow-lg" : "border border-hair"}`}>
              {pk.badge && <span className="absolute -top-3 right-4 rounded-full bg-brand-yellow text-ink text-[11px] font-semibold px-3 py-1">{pk.badge}</span>}
              <div className="font-heading text-xl text-ink">{pk.name}</div>
              {pk.text && <div className="mt-1.5 text-sm text-ink-soft">{pk.text}</div>}
            </div>
          ))}
        </div>
      )}
      {(d.footer_chips || []).length > 0 && (
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {d.footer_chips.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-hair text-sm text-ink-soft">
              <Check className="w-3.5 h-3.5 text-brand-yellow" strokeWidth={3}/> {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Price Cards (transparente Startpreise als Karten) ----
function PriceCardsSection({ section }) {
  const d = section.data || {};
  const items = d.items || [];
  return (
    <div>
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} subtitle={d.subtitle} className="mx-auto text-center" />
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {items.map((it, i) => (
          <div key={i}
            className="rounded-2xl border border-hair bg-white p-6 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all">
            <div className="font-heading text-lg text-ink">{it.name}</div>
            <div className="mt-3">
              <div className="text-xs text-ink-muted uppercase tracking-widest">ab</div>
              <div className="font-heading text-4xl text-brand-yellow leading-none">{it.from_price}</div>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-ink-soft flex-1">
              {(it.features || []).map((f, j) => (
                <li key={j} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-yellow mt-1 shrink-0" strokeWidth={3}/>{f}</li>
              ))}
            </ul>
            <Link to={it.href || "/fotoboxen"} className="mt-5 text-sm font-medium text-ink hover:text-brand-yellow inline-flex items-center gap-1">
              Details ansehen <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          </div>
        ))}
      </div>
      {d.footnote && <div className="mt-8 max-w-3xl mx-auto text-center text-sm text-ink-muted leading-relaxed">{d.footnote}</div>}
      {d.cta_label && (
        <div className="mt-8 flex justify-center">
          <Link to={d.cta_href || "/kontakt"} className="btn-accent">{d.cta_label} <ArrowRight className="w-4 h-4"/></Link>
        </div>
      )}
    </div>
  );
}

// ---- Gallery Marquee (endlose Bildergalerie) ----
function GalleryMarqueeSection({ section }) {
  const d = section.data || {};
  const imgs = d.images || [];
  const doubled = [...imgs, ...imgs];
  return (
    <div>
      <SectionHeader eyebrow={d.eyebrow} title={d.title} title_accent={d.title_accent} />
      <div className="mt-10 overflow-hidden" style={{ maskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)" }}>
        <div className="flex gap-4 animate-marquee" style={{ animationDuration: "60s" }}>
          {doubled.map((img, i) => (
            <div key={i} className="shrink-0 w-56 h-72 md:w-64 md:h-80 rounded-2xl overflow-hidden bg-cream">
              {img.url && <img {...imgSet(img.url, "(max-width: 768px) 100vw, 50vw")} alt={img.alt || ""} className="w-full h-full object-cover" loading="lazy" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Reviews CTA (Google-Bewertungen Highlight) ----
function ReviewsCtaSection({ section }) {
  const d = section.data || {};
  return (
    <div className="text-center max-w-3xl mx-auto">
      {d.eyebrow && <div className="pill-badge mx-auto">{d.eyebrow}</div>}
      <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-[1.05]">
        {d.title} {d.title_accent && <span className="font-serif-italic text-brand-yellow">{d.title_accent}</span>}
      </h2>
      <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-6">
        <div className="flex items-center gap-3">
          <div className="font-heading text-6xl text-brand-yellow">{d.rating || "5.0"}</div>
          <div className="text-left">
            <div className="flex gap-0.5">{[0,1,2,3,4].map((i)=>(<Star key={i} className="w-5 h-5" strokeWidth={0} fill="#F4B84A"/>))}</div>
            <div className="text-sm text-ink-soft mt-1">{d.count || "130"} Bewertungen</div>
          </div>
        </div>
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {d.cta_label && <Link to={d.cta_href || "#"} className="btn-secondary">{d.cta_label}</Link>}
        {d.secondary_cta_label && <Link to={d.secondary_cta_href || "/kontakt"} className="btn-accent">{d.secondary_cta_label} <ArrowRight className="w-4 h-4"/></Link>}
      </div>
    </div>
  );
}

// ---- Long Text (SEO-Content) ----
function LongTextSection({ section }) {
  const d = section.data || {};
  const [open, setOpen] = useState(false);
  if (d.collapsible) {
    // Eingeklappt: alle Absätze bleiben im HTML (für Suchmaschinen), sichtbar ist zunächst nur der erste.
    const paras = d.paragraphs || [];
    return (
      <div className="max-w-3xl mx-auto text-center" data-testid="long-text-collapsible">
        {d.title && <h2 className="font-heading text-2xl md:text-3xl text-ink">{d.title}</h2>}
        <div className="mt-4 space-y-4 text-ink-soft leading-relaxed">
          {paras.map((p, i) => <p key={i} className={i > 0 && !open ? "hidden" : ""}>{p}</p>)}
        </div>
        {paras.length > 1 && (
          <button type="button" onClick={() => setOpen(!open)} className="mt-3 text-sm underline underline-offset-4 text-ink hover:text-brand-yellow" aria-expanded={open}>
            {open ? "weniger anzeigen" : "weiterlesen"}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr,2fr] gap-10 items-start">
      <div className="lg:sticky lg:top-24">
        <div className="pill-badge">FACHHANDEL</div>
        {d.title && <h2 className="font-heading text-4xl md:text-5xl text-ink mt-4 leading-[1.05]">{d.title}</h2>}
        <div className="mt-6 h-1 w-16 bg-brand-yellow rounded-full" />
      </div>
      <div className="space-y-5">
        {(d.paragraphs || []).map((p, i) => (
          <p key={i} className="text-ink-soft text-lg leading-relaxed first:text-xl first:text-ink first:font-medium">
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}

function VideoSection({ section }) {
  const d = section.data || {};
  const player = useRef(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = d.url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${d.url}` : d.url;
  const poster = d.poster?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${d.poster}` : d.poster;
  if (!src) return null;
  return (
    <div className="contrast-dark relative overflow-hidden rounded-3xl p-6 py-8 md:p-12 text-white border border-white/10"
      style={{ background: "linear-gradient(120deg, #14120F 0%, #221C15 52%, #1C1915 100%)" }} data-testid={`video-section-${section.id}`}>
      <div className="absolute -top-24 -right-16 w-[480px] h-[480px] rounded-full pointer-events-none opacity-40" style={{ background: "radial-gradient(circle, rgba(244,184,74,0.28) 0%, transparent 65%)" }} />
      <div className="relative z-10">
        <h2 className="font-heading text-3xl md:text-4xl text-white text-center mb-6 md:mb-8" id={`video-title-${section.id}`} data-testid={`video-title-${section.id}`}>{d.title}</h2>
        <video ref={player} src={src} poster={poster} controls playsInline preload="none"
          aria-labelledby={`video-title-${section.id}`} data-testid={`video-player-${section.id}`}
          className="block mx-auto h-[min(50svh,360px)] md:h-[480px] w-auto max-w-full rounded-xl bg-black object-contain shadow-2xl"
          width="720" height="1280"
          onWaiting={() => setLoading(true)} onPlaying={() => setLoading(false)}
          onCanPlay={() => { setLoading(false); setFailed(false); }} onError={() => { setFailed(true); setLoading(false); }}>
          Ihr Browser unterstützt die Videowiedergabe nicht.
        </video>
        {d.url === "/api/site-media/showroom-video" && (
          <p className="mt-5 max-w-2xl mx-auto text-sm leading-relaxed text-white/80" data-testid="showroom-video-academy-status">
            Hinweis zur Fotobox Academy Pro: Der Bereich befindet sich derzeit im Aufbau. Die Online-Freischaltung wird vorbereitet; Schulungsvideos und Anleitungen werden anschließend nach und nach veröffentlicht.{" "}
            <Link to="/fotobox-pro-academy" className="text-brand-yellow underline underline-offset-4" data-testid="showroom-video-academy-link">Mehr zur Academy erfahren</Link>
          </p>
        )}
        {loading && <p role="status" className="mt-3 text-center text-sm text-white/80" data-testid={`video-loading-${section.id}`}>Video wird geladen …</p>}
        {failed && <div role="alert" className="mt-4 text-center text-sm text-white" data-testid={`video-error-${section.id}`}>
          <p>Das Video konnte nicht geladen werden.</p>
          <button type="button" className="mt-2 underline underline-offset-4 hover:text-brand-yellow"
            data-testid={`video-retry-${section.id}`} onClick={() => {
              setFailed(false); setLoading(true); player.current?.load();
              player.current?.play().catch(() => { setLoading(false); setFailed(true); });
            }}>Erneut laden</button>
        </div>}
      </div>
    </div>
  );
}

const RENDERERS = {
  video: VideoSection,
  hero: HeroSection,
  text: TextSection,
  text_image: TextImageSection,
  product_grid: ProductGridSection,
  features: FeaturesSection,
  faq: FaqSection,
  cta_band: CtaBandSection,
  stats: StatsSection,
  spacer: SpacerSection,
  product_finder: ProductFinderTeaser,
  marquee: MarqueeSection,
  steps: StepsSection,
  price_cards: PriceCardsSection,
  gallery_marquee: GalleryMarqueeSection,
  reviews_cta: ReviewsCtaSection,
  long_text: LongTextSection,
  ...HOME_RENDERERS,
};

// Sektionen, die ausschließlich auf Desktop gezeigt werden.
// Werden auf mobilen Viewports (<md) ausgeblendet.
function _isMobileHidden(section) {
  if (!section) return false;
  const type = section.type;
  const title = (section.data && section.data.title) || "";
  if (type === "price_cards") return true;
  if (type === "long_text" && !section.data?.collapsible && /wiederverkauf/i.test(title)) return true;
  return false;
}

export default function PageRenderer({ sections = [] }) {
  return (
    <>
      {sections.map((s) => {
        if (!s.visible && s.visible !== undefined) return null;
        const Comp = RENDERERS[s.type];
        if (!Comp) return null;
        if (BARE_TYPES.has(s.type)) return <Comp key={s.id} section={s} />;
        const mobileHidden = _isMobileHidden(s);
        return (
          <div key={s.id} className={mobileHidden ? "hidden md:block" : undefined}>
            <SectionWrapper section={s}>
              <Comp section={s} />
            </SectionWrapper>
          </div>
        );
      })}
    </>
  );
}
